import { Injectable } from '@angular/core';
import { TapticEngine } from '@awesome-cordova-plugins/taptic-engine/ngx';
import { Vibration } from '@awesome-cordova-plugins/vibration/ngx';
import { LoadingController, AlertController, ToastController, ModalController, PopoverController, Platform, ActionSheetController, NavController} from '@ionic/angular';
import { ComponentProps, ComponentRef, OverlayEventDetail } from '@ionic/core';
import { HttpFunctionResult } from '../_interfaces/HttpFunctionResult';
import { Keyboard } from '@capacitor/keyboard';
import { BehaviorSubject, Subscription } from 'rxjs';
import { EditArt } from '../_interfaces/EditArt';
import { NewFeaturesPage } from '../_pages/new-features/new-features.page';
import { environment } from 'src/environments/environment';
import { StorageManagerService } from './storage-manager.service';

export enum MsgType {
  Info,
  Error
}

export enum VibrateType {
  light = "light",
  medium = "medium",
  heavy = "heavy"
}

export type EventLike = { target: EventTarget }

export interface AFSPopoverOptions<T>{
  component: ComponentRef,
  event: Event | EventLike, 
  componentProps?: ComponentProps<ComponentRef>,
  backdropDismiss?: boolean
  showBackdrop?: boolean
  cssClass?: string | undefined
}

export enum AFSInputType{
  TEXT = "text",
  NUMBER = "number",
  PASSWORD = "password"
}

export interface AFSInputOptions{
  message?: string, 
  header: string, 
  autofocus?: boolean, 
  placeholder?: string, 
  backdropDismiss?: boolean,
  initialText?: string,
  type: AFSInputType
}


export interface AFSModalOptions<T>{
  component: ComponentRef, 
  id?: string
  componentProps?: ComponentProps<ComponentRef>,
  backdropDismiss?: boolean,
  breakpoints?: number[] | undefined,
  cssClass?: string | undefined
}


export interface AFSActionsheetOptions{
  header?: string
  buttons: AFSActionsheetButton[],
}

export interface AFSActionsheetButton{
  description: string
  danger?: boolean
  action: () => void
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  private _loading = null;
  private loadingVisible: Boolean = false;
  private keyBoardState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null)


  public async presentActionSheet(header: string, buttons: EditArt[] ): Promise<HttpFunctionResult<EditArt>>{
    return new Promise<HttpFunctionResult<EditArt>>(async resolve=>{
      
      let actionsheet: HTMLIonActionSheetElement = await this.actionsheetCtrl.create({
        header: header,
        cssClass: "tsAlert",
        buttons: buttons.map(button => {
          return {
            text: button.description,
            id: String(button.id),
            data: button
          }
        })
      })

      actionsheet.onWillDismiss().then((result: any) => {
        if(!result.data) {
          resolve({success: false})
          return
        }else resolve({
          success: true,
          data: result.data as EditArt
        })
      })
  
      this.vibrate(VibrateType.medium)
      await actionsheet.present()
  
    })

  }


  constructor(
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private tapticEngine: TapticEngine,
    private platform: Platform,
    private navCtrl: NavController,
    private vibration: Vibration,
    private storageManager: StorageManagerService,
    private actionsheetCtrl: ActionSheetController
  ) {
    this.platform.ready().then(()=>{
      if(!platform.is("capacitor")) return
      Keyboard.addListener("keyboardDidShow", ()=>this.keyBoardState.next(true))
      Keyboard.addListener("keyboardDidHide", ()=>this.keyBoardState.next(false))
    })
  }

  /**
   * Öffnet einen Laden-Spinner und führt eine Funktion aus
   * @param {Promise<void>} func Funktion die ausfegührt werden soll
   * @param {string} message Nachricht die angezeigt werden soll (Standard: "Laden...")
   */
  public async showLoading(func: () => Promise<void>, message: string = "Laden..."){
    const loading = await this.loadingCtrl.create({
      message: message,
      cssClass: "tsAlert"
    })
    await loading.present()
    await func()
    await loading.dismiss()
  }

  /**
   * @deprecated use `showLoading` instead
   * @returns
   */
  get loading() {
    if (!this._loading) {
      this._loading = this.loadingCtrl.create({
        
      });
    }
    return this._loading;
  }


  private load 

  /**
   * Updatet den Laden-Text und erzeigt einen Laden-Spinner
   * @param newContent message text
   * @deprecated use `showLoading` instead
   * @returns 
   */
  async updateLoading(newContent: string) {
    await this.hideLoading()
    this.load = await this.loadingCtrl.create({
      message: newContent,
      cssClass: "tsAlert"
    })
    if (!this.loadingVisible) {
      this.loadingVisible = true;
      return await this.load.present();
    }
  }

  async hideLoading() {
    if (this.loadingVisible) {
      this.loadingVisible = false;
      if(this.load) await this.load.dismiss();
      this.load = null;
    }
  }

  public updateLoadingContent(newContent: string){
    if(!this.load) return
    this.load.message = newContent
  }

  async showMsg(Type: MsgType, Msg: string) {
    const prom = new Promise<boolean>(async resolve => {
      let title: string;
      switch (Type) {
        case MsgType.Info:
          title = "Information";
          break;
        case MsgType.Error:
          title = "Fehler";
          break;
      }
      const alert = await this.alertCtrl.create({
        header: title,
        message: Msg,
        backdropDismiss: false,
        cssClass: "tsAlert",
        buttons: [
          {
            text: "OK",
            handler: () => {
              resolve(true);
            }
          }
        ]
      });
      await alert.present();
    });
    return await prom;
  }

  async confirm(Msg: string, yesText: string = "Ja", noText: string = "Nein", header: string = "Bestätigen"): Promise<boolean> {
    const prom = new Promise<boolean>(async resolve => {
      const alert = await this.alertCtrl.create({
        header: header,
        message: Msg,
        cssClass: "tsAlert",
        backdropDismiss: false,
        buttons: [
          {
            text: yesText,
            id: "ts-alert-yes",
            handler: () => {
              resolve(true);
            }
          },
          {
            text: noText,
            role: "cancel",
            cssClass: "danger",
            handler: () => {
              resolve(false);
            }
          }
        ]
      });

      //Enter yes auswählen
      const yesButton = document.getElementById("ts-alert-yes")
      if(yesButton) setTimeout(() => yesButton.focus(), 200);
      

      await alert.present();
    });
    await this.hideLoading();
    return await prom;
  }

  async showPromp(title: string, message: string, placeholder){
    return new Promise(async resolve=>{
      const alert = await this.alertCtrl.create({
        header: title,
        message: message,
        inputs: [
          {
            name: title,
            placeholder: placeholder
          }
        ],
        buttons:[
          {
            text: "Abbrechen",
            handler: ()=>{
              resolve(false)
            }  
          },
          {
            text: "OK",
            handler: data =>{
              resolve(data[title]) ;
            }
          }
        ]
      });
      await alert.present()
    })
  }

  /**
   * Öffnen eines Eingabefeldes
   * @param {AFSInputOptions} options Eingabe Optionen
   * @returns 
   */
  public async showInput<T>(options: AFSInputOptions): Promise<HttpFunctionResult<T>>{
    return new Promise(async resolve => {
      const alert = await this.alertCtrl.create({
        header: options.header,
        message: options?.message ?? "",
        cssClass: "tsAlert",
        backdropDismiss: options?.backdropDismiss ?? false,
        inputs: [{
          type: options.type,
          value: options?.initialText ?? "",
          placeholder: options?.placeholder ?? "",
        }],
        buttons: [
          { text: "Ok", role: "confirm", handler: (value: any)=>resolve({success: true, data: value[0] as T})},
          { text: "Abbrechen", role: "cancel", handler: ()=>resolve({success: false})}
        ]
      })

      await alert.present()

      //Backdrop behandeln
      if(options?.backdropDismiss) alert.onDidDismiss().then(async (result: OverlayEventDetail<any>)=>{
          await this.hideKeyboardOnIOS()
          if(result.role == "backdrop") return resolve({success: false})
        })
      
      //Focus setzen
      const input: any = document.querySelector('ion-alert input');
      if(options?.autofocus) input.focus()

      //Initialen Text setzen
      if(options?.initialText) input.value = options.initialText

      // Wenn enter gedrückt wird
      input.addEventListener('keydown', async (event) => {
        if (event.key === 'Enter'){
          resolve({success: true, data: input.value as T})
          await alert.dismiss()
        }
      });

    })
  }

  /**
   * 
   * @param message 
   * @param header 
   * @param autofocus 
   * @param placeholder 
   * @param type 
   * @deprecated use `showInput` instead
   * @returns 
   */
  public async getInputText(message: string, header: string, autofocus: boolean = false, placeholder: string = "", type: "text" | "number" | "password" = "text"): Promise<any>{
    return new Promise(async resolve=>{
      const alert = await this.alertCtrl.create({
        header: header,
        message: message,
        cssClass: "tsAlert",
        backdropDismiss: false,
        inputs: [
          {
            type: type,
            placeholder: placeholder
          }
        ],
        buttons: [
          {
            text: "Ok",
            handler: (value: any)=>{
              resolve(value[0])
            }
          },
          {
            text: "Abbrechen",
            handler: ()=>{
              resolve(null)
            }
          }
        ]
      })
      

      const input: any = document.querySelector('ion-alert input');
      if(autofocus){
        input.focus()
      }

      // Wenn enter gedrückt wird
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter'){
          resolve(input.value)
          alert.dismiss()
        }
      });

    })
  }


  private errToastShow: boolean = false
  public async showError(message: string, duration: number = 3000){
    if(this.errToastShow) return
    this.errToastShow = true
    const errorToast = await this.toastCtrl.create({
      header: "Fehler",
      message: message,
      duration: duration,
      position: "top",
      keyboardClose: false,
      cssClass: "errorToast",
      icon: "fa-exclamation",
      mode: "ios",
      buttons: [{
        side: "end",
        icon: "fa-x-mark",
        role: "cancel"
      }]
    })
    
    errorToast.removeAttribute('tabindex'); 
    errorToast.onDidDismiss().then(()=>this.errToastShow = false)
    await errorToast.present()
  }

  public async showInformationToast(message: string, header: string = "Information", duration: number = 5000){
    const toast = await this.toastCtrl.create({
      header: header,
      message: message,
      icon: "fa-information",
      cssClass: "informationToast",
      duration: duration,
      position: "top",
      mode: "ios",
      buttons: [{
        side: "end",
        icon: "fa-x-mark",
        role: "cancel"
      }]
    })
    toast.removeAttribute('tabindex'); 
    await toast.present()
  }

  /**
   * Öffnet ein modal und gibt das Ergebnis zurück
   * @param {AFSModalOptions<T>} options Modal Optionen
   * @returns 
   */
  public async showModal<T>(options: AFSModalOptions<T>): Promise<HttpFunctionResult<T>>{

    return new Promise(async resolve => {
      const modal = await this.modalCtrl.create({
        id: options?.id ?? undefined,
        component: options.component,
        componentProps: options.componentProps,
        keyboardClose: true,
        cssClass: options.cssClass,
        backdropDismiss: options.backdropDismiss ?? true,
        initialBreakpoint: options.breakpoints ? options.breakpoints[0] : undefined,
        breakpoints: options.breakpoints ?? undefined
      })

      if( modal?.componentProps) modal.componentProps.modalRef = modal

      modal.onWillDismiss().then(async (result: any)=>{
        await this.hideKeyboardOnIOS()
        if(!(result.data as T)) return resolve({success: false})
        else return resolve({ success: true, data: result.data as T })
      })

      this.vibrate(VibrateType.medium)
      await modal.present()

    })

  }

  /**
   * * @deprecated use `showModal` instead
   * @param component 
   * @param componentProps 
   * @param resolve 
   * @param backdropDismiss 
   * @param breakpoints 
   * @param cssClass 
   */
  public async openModal<T>(
    component: ComponentRef, 
    componentProps?: ComponentProps<ComponentRef>,
    resolve?: (result: HttpFunctionResult<T>)=>void,
    backdropDismiss: boolean = true,
    breakpoints: number[] = undefined,
    cssClass: string | undefined = undefined
    ){
    const modal = await this.modalCtrl.create({
      component,
      componentProps,
      keyboardClose: true,
      cssClass,
      backdropDismiss,
      initialBreakpoint: breakpoints ? breakpoints[0] : undefined,
      breakpoints
    })
    if( modal?.componentProps) modal.componentProps.modalRef = modal

    modal.onWillDismiss().then(async (result: any)=>{
      await this.hideKeyboardOnIOS()
      if(!(result.data as T)) {
        if(resolve) {
          resolve({success: false})
          return
        } 
      }
      else{
        resolve({
          success: true,
          data: result.data as T
        })
        return
      }
    })

    this.vibrate(VibrateType.medium)
    await modal.present()

  } 

  /**
   * @deprecated use `showPopover` instead
   * @param component 
   * @param event 
   * @param componentProps 
   * @param resolve 
   * @param backdropDismiss 
   * @param showBackdrop 
   * @param cssClass 
   */
  public async openPopover<T>(
    component: ComponentRef,
    event: Event | EventLike, 
    componentProps?: ComponentProps<ComponentRef>,
    resolve?: (result: HttpFunctionResult<T>)=>void,
    backdropDismiss: boolean = true,
    showBackdrop: boolean = true,
    cssClass: string | undefined = undefined
  ){
    const popover = await this.popoverCtrl.create({
      component: component,
      componentProps: componentProps,
      event: event as Event,
      showBackdrop: showBackdrop,
      keyboardClose: true,
      cssClass,
      mode: "md",
      backdropDismiss: backdropDismiss
    })

    popover.onWillDismiss().then((result: any)=>{

      if(!result.data){
        if(resolve){
          resolve({success: false})
          return
        } 
      }
      else{
        resolve({
          success: true,
          data: result.data.item as T
        })
        return
      }
    })

    this.vibrate(VibrateType.medium)

    await popover.present()
  }

  /**
   * Öffnet ein popover
   * @param {AFSPopoverOptions<T>} options Popover Optionen
   */
  public async showPopover<T>(options: AFSPopoverOptions<T>): Promise<HttpFunctionResult<T>>{

    return new Promise<HttpFunctionResult<T>>(async resolve=>{

      const popover = await this.popoverCtrl.create({
        component: options.component,
        componentProps: options?.componentProps,
        event: options.event as Event,
        showBackdrop: options.showBackdrop ?? true,
        keyboardClose: true,
        cssClass: options?.cssClass,
        mode: "md",
        backdropDismiss: options?.backdropDismiss ?? true
      })
  
      popover.onWillDismiss().then((result: any)=>{
        if(!result.data) return resolve({success: false})
        else return resolve({ success: true, data: result.data.item as T})
      })
  
      this.vibrate(VibrateType.medium)
  
      await popover.present()

    })
  }

  /**
   * Öffnet ein ActionSheet
   * @param {AFSActionsheetOptions} options ActionSheet Optionen
   */
  public async showActionSheet(options: AFSActionsheetOptions): Promise<HttpFunctionResult<AFSActionsheetButton>>{
    return new Promise(async resolve=>{
      const actionsheet: HTMLIonActionSheetElement = await this.actionsheetCtrl.create({
        header: options?.header || "",
        cssClass: "actionsheet-alert",
        buttons: options.buttons.map(button => ({
          text: button.description,
          cssClass: button?.danger ? "actionsheet-danger" : "",
          handler: () => {
            button.action()
            resolve({success: true, data: button})
          }
        }))
      })

      actionsheet.onWillDismiss().then((result: OverlayEventDetail<any>) => {
        if(result.role == "backdrop") return resolve({success: false})
      })

      this.vibrate(VibrateType.medium)
      await actionsheet.present()

    })
  }

  public async vibrate(iosStyle:  VibrateType = VibrateType.medium, androidDuration: number = 15){
    if(!this.platform.is("capacitor")) return
    if(this.platform.is("ios")) await this.tapticEngine.impact({style: iosStyle})
    else  this.vibration.vibrate(androidDuration)
  }

  public async hideKeyboardOnIOS(){
    return new Promise(async resolve=>{
      if(this.keyBoardState.value && this.platform.is("ios")){
        await Keyboard.hide()
        const sub = this.keyBoardState.subscribe((val: boolean)=>{
          setTimeout(() => {
            resolve(true)
            sub.unsubscribe()
          }, 150);
        })
      }else resolve(true)
    })
  }

  public showNewFeaturesWhenNewer(delay: number = 3000){
    setTimeout(async () => {
      if(await this.storageManager.getItem<string>("NEW_FEATURES_LAST_VERSION") == environment.version) return
      await this.storageManager.setItem<string>("NEW_FEATURES_LAST_VERSION", environment.version)
      await this.openModal(NewFeaturesPage)
    }, delay);
  }

  public qrCodeResult: BehaviorSubject<HttpFunctionResult<string>> = new BehaviorSubject<HttpFunctionResult<string>>(null)

  public async getQRCodeData(): Promise<HttpFunctionResult<string>>{
    return new Promise(async resolve=>{
      await this.navCtrl.navigateForward("qrscanner", {animated: false})
      const sub: Subscription = this.qrCodeResult.asObservable().subscribe((data: HttpFunctionResult<string>)=>{
        if(data == null) return
        sub.unsubscribe()
        this.qrCodeResult = new BehaviorSubject<HttpFunctionResult<string>>(null)
        resolve({success: true, data: data.data})
      })
    })
  }



}